import { CenteredFadeInSpinner, KeyOn, useAsyncFetch } from '@tableau/vizportalclient-common-components';
import React from 'react';
import { ConnectedExplorer } from '../../app/spaces/Explorer/ConnectedExplorer';
import { Messages } from '../../codegen/Localize';
import { cssClass, fillStyle, flexColumnStyle } from '../../style';
import { DataConnectionDefinition } from '../../contentDefinitions/DataConnectionDefinition';
import { FlowOutputStepRunListItemInfo } from '../../ts/models/FlowOutputStepRunInfo';
import { FlowInputStep } from '../../ts/ServerApi/types/FlowInputStep';
import { FlowOutputStep } from '../../ts/ServerApi/types/FlowOutputStep';
import { FlowOutputStepRuns } from '../resources/FlowOutputStepRuns';
import { Flows } from '../resources/Flows';
import { FlowSubplaceProps } from '../types/FlowSubplaceProps';


const cssPrefix = 'FlowConnectionsSubplace';

const rootClass = cssClass(cssPrefix, 'root', fillStyle, flexColumnStyle);
const connectionsClass = cssClass(cssPrefix, 'connections', {
  flex: '1',
  display: 'flex',
  position: 'relative',
});

export type FlowConnectionsSubplaceMetadata = {
  readonly subplaceMetadata: {
    readonly inputSteps: ReadonlyArray<FlowInputStep>;
    readonly outputSteps: ReadonlyArray<FlowOutputStep>;
    readonly mostRecentRuns: ReadonlyArray<FlowOutputStepRunListItemInfo>;
    readonly flowAllowedActions: FlowSubplaceProps['flowAllowedActions'];
  };
};

function FlowConnectionsSubplaceFC(props: FlowSubplaceProps): JSX.Element {

  // The flow connections grid cells require some extra information about the flow as a whole.
  // Ordinarily, we'd load this stuff in the Space provider (common to all subspaces), but since
  // there are three different queries, we're lazily loading them on only the relevant subspace.
  //
  // In order to get that information into the grid cells, we wait until we've fetched all the
  // pieces, then add it to the flow on the spaceInfo and reënter the space.
  //
  // Ideally, we'd show the explorer immediately to give the user some UI feedback and "plug in"
  // the extra metadata to the grid rows once it's available (or wait until the metadata is ready
  // before rendering the grid rows), but the architecture doesn't provide a way to do that without
  // resetting the explorer state when we call enterSpace a second time. So, instead, we block the
  // explorer from loading at all until the metadata is fetched.

  const {
    data: subspaceMetadata,
    loading: loadingSubspaceMetadata
  } = useAsyncFetch(fetchSubspaceMetadata, props.flowDetailInfo.id);
  const flowAllowedActions = props.flowAllowedActions;

  React.useEffect(() => {
    if (subspaceMetadata && !loadingSubspaceMetadata) {
      props.enterSpace({
        ...props.flowDetailInfo,
        subplaceMetadata: { ...subspaceMetadata, flowAllowedActions }
      });
    }
  }, [subspaceMetadata, flowAllowedActions]);

  return (
    <div className={rootClass}>
      <div className={connectionsClass}>
        { subspaceMetadata && !loadingSubspaceMetadata ? (
          <ConnectedExplorer
            angularContext={props.angularContext}
            supportedDisplayModes={DataConnectionDefinition.supportedDisplayModes}
            hideSelectionAndActionMenu={false}
            toolbarProps={{
              supportFilterPanel: false,
              hideSelection: false,
              toolbarVisibility: 'enabled',
            }}
            emptyStateProps={{
              primaryMessage: Messages.flowOutputsEmptyMessage()
            }}
          />
        ) : <CenteredFadeInSpinner />}
      </div>
    </div>
  );
}

type AsyncMetadata = Omit<FlowConnectionsSubplaceMetadata['subplaceMetadata'], 'flowAllowedActions'>;

async function fetchSubspaceMetadata(flowId: string): Promise<AsyncMetadata> {
  const results = await Promise.all([
    Flows.getInputSteps(flowId),
    Flows.getOutputSteps(flowId),
    FlowOutputStepRuns.getMostRecentForFlow(flowId, true),
  ]);
  return {
    inputSteps: results[0],
    outputSteps: results[1],
    mostRecentRuns: results[2],
  };
}

export const FlowConnectionsSubplace = KeyOn(
  FlowConnectionsSubplaceFC,
  props => props.flowDetailInfo.id
);
